import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { AtlassianLogo } from '@atlaskit/logo';
import { B200, B400 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import {
  COFSProductActivateResponse,
  GetLicenseInformationProduct,
  ProductActivateResponse
} from '@atlassiansox/asc-stargate';
import {
  Environment,
  OfferingCatalog,
  ProductKey as Productkeys,
  SanitizedFormattedHtmlMessage,
  useStargateClient
} from '@atlassiansox/bxpkit-core';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FreeBundlesDataProvider } from '../hooks';
import { WacFreeBundlesPropsType } from '../model';
import { messages } from './messages';
import { PrimaryProductDisplayView } from './primary-product-display';
import { SecondaryProductSelectList } from './secondary-product-select-list';
import {
  AgreeButton,
  FeatureListContainer,
  HeaderLevel3,
  HeaderLevel4,
  HoverDialog,
  LargerTeamCTAContainer,
  LargerTeamCTACopyContainer,
  LeftColumn,
  MainHeading,
  NextAndSkipButtonContainer,
  NextButton,
  NextButtonContainer,
  NoCreditCardRequiredText,
  RightColumn,
  Root,
  SkipButton,
  SubHeading,
  TermsAndConditionsText,
  Wrapper
} from './styled';
import { WacFreeBundlesModal } from './wac-free-bundles-modal';

type BundlePostSignupSecondaryProductOverrideType = {
  readonly productKeys: string[];
  readonly primaryProductKey: string;
};

const offeringCatalog = new OfferingCatalog(Environment.getEnvironment());

export function BundlePostSignupSecondaryProductOverride({
  productKeys,
  primaryProductKey
}: BundlePostSignupSecondaryProductOverrideType): string[] {
  const isJSM = primaryProductKey === Productkeys.JIRA_SERVICE_MANAGEMENT;
  const isJSW = primaryProductKey === Productkeys.JIRA_SOFTWARE;
  // JSM bundles
  if (isJSM && !productKeys.includes(Productkeys.JIRA_SOFTWARE)) {
    return [Productkeys.JIRA_SOFTWARE];
  } else if (isJSM && productKeys.includes(Productkeys.JIRA_SOFTWARE)) {
    return [];
  }

  // JSW bundles
  if (
    isJSW &&
    productKeys.includes(Productkeys.JIRA_SERVICE_MANAGEMENT) &&
    productKeys.includes(Productkeys.CONFLUENCE)
  ) {
    return [];
  }
  if (isJSW && productKeys.includes(Productkeys.CONFLUENCE)) {
    return [Productkeys.JIRA_SERVICE_MANAGEMENT];
  }
  if (isJSW && productKeys.includes(Productkeys.JIRA_SERVICE_MANAGEMENT)) {
    return [Productkeys.CONFLUENCE];
  }
  if (
    isJSW &&
    !(
      productKeys.includes(Productkeys.JIRA_SERVICE_MANAGEMENT) ||
      productKeys.includes(Productkeys.CONFLUENCE)
    )
  ) {
    return [Productkeys.CONFLUENCE, Productkeys.JIRA_SERVICE_MANAGEMENT];
  }
  return [];
}

export function WacFreeBundlesView({
  primaryProductKey,
  secondaryProductKeys,
  signUpPath,
  bundleMap,
  comparePlansUrl,
  productData,
  featureFlags,
  bundleProvisioningStatus,
  setBundleProvisioningStatus,
  isModalOpen,
  setIsModalOpen,
  cloudIdentifier
}: WacFreeBundlesPropsType) {
  const { createAnalyticsEvent } = useAnalyticsEvents();
  useEffect(() => {
    createAnalyticsEvent({
      actionSubject: 'bundles component',
      action: 'loaded',
      eventName: 'bundlesComponentLoaded',
      eventComponent: 'bxpkit-wac-free-bundles',
      event: 'load',
      eventType: 'UI'
    }).fire();
  }, []);

  return (
    <WacFreeBundlesRoot
      primaryProductKey={primaryProductKey}
      secondaryProductKeys={secondaryProductKeys}
      signUpPath={signUpPath}
      bundleMap={bundleMap}
      comparePlansUrl={comparePlansUrl}
      productData={productData}
      featureFlags={featureFlags}
      bundleProvisioningStatus={bundleProvisioningStatus}
      setBundleProvisioningStatus={setBundleProvisioningStatus}
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      cloudIdentifier={cloudIdentifier}
    />
  );
}

function WacFreeBundlesRoot({
  primaryProductKey,
  secondaryProductKeys,
  signUpPath,
  bundleMap,
  comparePlansUrl,
  productData,
  featureFlags,
  bundleProvisioningStatus,
  setBundleProvisioningStatus,
  isModalOpen,
  setIsModalOpen,
  cloudIdentifier
}: WacFreeBundlesPropsType) {
  const [updateConfirmationPage, setUpdateConfirmationPage] = useState('');
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const [selectedSecondaryProductKey, setSelectedSecondaryProductKey] =
    useState('');
  const [secondaryProductKeysOverride] = useState(secondaryProductKeys);
  const queryParameters = new URLSearchParams(window.location.search) || {};
  const { enableBundlesAfterSignup, enableBundlesPostSignupConfluence } =
    featureFlags;
  const continueUrl = queryParameters.get('continue');
  const [cloudId] = useState(queryParameters.get('cloudId') ?? cloudIdentifier);
  const teamType = queryParameters.get('team_type');
  const stargateClient = useStargateClient();
  const onExperimentPage = enableBundlesAfterSignup && cloudId;
  const [isSecondaryProductActivating, setSecondaryProductActivating] =
    useState(false);

  /**
   * bundles after signup experiment
   * */
  const provisionProduct = async () => {
    setSecondaryProductActivating(true);
    const licenseInfo = !!cloudId
      ? await stargateClient.getLicenceInformationStatus({
          cloudId,
          webPlatform: 'magnolia'
        })
      : null;
    const ccpProducts = offeringCatalog.mapProductKeyAndEditionToCcpProduct({
      productKey: selectedSecondaryProductKey as Productkeys,
      edition: 'free' as string
    });

    const existingProd = Object.values(licenseInfo?.products ?? {}).find(
      (prod) => !!prod.ccpTransactionAccountId && !!prod.ccpEntitlementId
    );

    let productActivationPromise: Promise<
      COFSProductActivateResponse | ProductActivateResponse
    >;

    const postCOFSProductActivate = () =>
      stargateClient.postCOFSProductActivate({
        cloudId: cloudId || '',
        productKey: selectedSecondaryProductKey,
        edition: 'free',
        webPlatform: 'magnolia'
      });

    const postCCPProductActivate = (
      licenseInfo: GetLicenseInformationProduct
    ) =>
      stargateClient.postProductActivate({
        offerings: [ccpProducts],
        transactionAccountId: licenseInfo.ccpTransactionAccountId,
        entitlementId: licenseInfo.ccpEntitlementId || '',
        cloudId: cloudId || '',
        webPlatform: 'magnolia'
      });

    productActivationPromise = existingProd
      ? postCCPProductActivate(existingProd)
      : postCOFSProductActivate();

    return productActivationPromise
      .catch(() => undefined)
      .finally(() => setSecondaryProductActivating(false));
  };

  useEffect(() => {
    async function checkProvisioning() {
      switch (bundleProvisioningStatus) {
        case 'provisioning':
          if (selectedSecondaryProductKey !== '') {
            // Specific to confluence don't provision until the user has actually agreed
            if (
              enableBundlesPostSignupConfluence &&
              updateConfirmationPage !== 'agree'
            ) {
              return;
            }

            const product = await provisionProduct();
            if (product && setBundleProvisioningStatus) {
              setBundleProvisioningStatus('success');
            }
          }
          return;
        case 'success':
          return;
      }
    }

    checkProvisioning();
  }, [bundleProvisioningStatus]);

  useEffect(() => {
    if (updateConfirmationPage === 'agree' && setBundleProvisioningStatus) {
      setBundleProvisioningStatus('payload-ready');
    }
    if (
      updateConfirmationPage === 'skip' &&
      setBundleProvisioningStatus &&
      !enableBundlesPostSignupConfluence
    ) {
      setBundleProvisioningStatus('success');
    }
  }, [updateConfirmationPage]);
  /*
    bundleMap structure is going to be as below
    {
    'jira-software.ondemand': 'jira-software',
    'jira-software.ondemand,confluence.ondemand': 'jira-software-confluence',
    'jira-software.ondemand,jira-servicedesk.ondemand':'jira-software-jira-service-management',
    'jira-servicedesk.ondemand': 'jira-service-management',
    'jira-servicedesk.ondemand,confluence.ondemand':'jira-service-management-confluence',
    'jira-core.ondemand': 'jira-core',
    'jira-core.ondemand,confluence.ondemand': 'jira-core-confluence',
    'confluence.ondemand': 'confluence',
    'confluence.ondemand,jira-servicedesk.ondemand':'confluence-jira-service-management',
  };
   */
  const handleAgreeAndSignup = async () => {
    /*
     * bundles post signup
     * **/
    // consume continue URL and fire analytics event and provision
    if (selectedSecondaryProductKey !== '') {
      createAnalyticsEvent({
        eventName: 'bundleAfterSignupAgreeProductsSelected',
        eventComponent: 'button',
        event: 'clicked',
        cloudId,
        continueUrl,
        selectedSecondaryProductKey
      }).fire();
    } else {
      createAnalyticsEvent({
        eventName: 'bundleAfterSignupAgreeNoProductSelected',
        eventComponent: 'button',
        event: 'clicked',
        cloudId,
        continueUrl
      }).fire();
    }

    if (
      enableBundlesPostSignupConfluence &&
      selectedSecondaryProductKey !== '' &&
      setBundleProvisioningStatus
    ) {
      setUpdateConfirmationPage('agree');
      return;
    }

    if (
      bundleProvisioningStatus === 'waiting' &&
      selectedSecondaryProductKey !== ''
    ) {
      setUpdateConfirmationPage('agree');
      return;
    }
    if (
      bundleProvisioningStatus === 'waiting' &&
      selectedSecondaryProductKey === ''
    ) {
      setUpdateConfirmationPage('skip');
      return;
    }
    if (
      bundleProvisioningStatus === undefined &&
      selectedSecondaryProductKey === ''
    ) {
      if (continueUrl && !isModalOpen) {
        window.location.href = continueUrl;
        return;
      }
    } else if (bundleProvisioningStatus === undefined) {
      const product = await provisionProduct();
      if (product && continueUrl && !isModalOpen) {
        window.location.href = continueUrl;
      }
      return;
    }

    // end post sign up logic
  };

  const handleNext = () => {
    createAnalyticsEvent({
      eventName: 'bundleBeforeSignupNext',
      eventComponent: 'button',
      event: 'clicked',
      cloudId
    }).fire();

    let bundleName = '';
    let createdBundleKey = primaryProductKey;
    if (selectedSecondaryProductKey) {
      createdBundleKey = primaryProductKey + ',' + selectedSecondaryProductKey;
    }
    if (createdBundleKey in bundleMap) {
      bundleName = bundleMap[createdBundleKey];
    }

    window.location.href = teamType
      ? `${Environment.getWacIntegrationUrl()}${signUpPath}?bundle=${bundleName}&edition=free&team_type=${teamType}`
      : `${Environment.getWacIntegrationUrl()}${signUpPath}?bundle=${bundleName}&edition=free`;
  };

  const handleSkip = () => {
    createAnalyticsEvent({
      eventName: 'bundleAfterSignupSkip',
      eventComponent: 'button',
      event: 'clicked',
      cloudId
    }).fire();

    if (bundleProvisioningStatus === 'waiting' && setBundleProvisioningStatus) {
      setUpdateConfirmationPage('skip');
      return;
    }
    if (continueUrl) {
      window.location.href = continueUrl;
    }
  };

  const handleComparePlans = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();

    createAnalyticsEvent({
      eventName: 'bundleComparePlans',
      eventComponent: 'button',
      event: 'clicked',
      cloudId
    }).fire();

    window.location.href = comparePlansUrl;
  };

  const ButtonGroup = () => {
    if (onExperimentPage) {
      return (
        <>
          <NextAndSkipButtonContainer
            className={secondaryProductKeysOverride.length > 1 ? 'expand' : ''}
          >
            <SkipButton
              data-testid={'skip-btn'}
              appearance="subtle"
              onClick={handleSkip}
            >
              <FormattedMessage {...messages.skipButtonLabel} />
            </SkipButton>
            <AgreeButton
              data-testid={'agree-btn'}
              appearance="primary"
              onClick={handleAgreeAndSignup}
              isLoading={isSecondaryProductActivating}
            >
              <FormattedMessage {...messages.agreeAndContinue} />
            </AgreeButton>
          </NextAndSkipButtonContainer>
          <TermsAndConditionsText>
            <SanitizedFormattedHtmlMessage
              {...messages.termsAndConditionsLabel}
            />
          </TermsAndConditionsText>
        </>
      );
    } else {
      return (
        <NextButtonContainer
          className={secondaryProductKeysOverride.length > 1 ? 'expand' : ''}
        >
          <NextButton
            data-testid={'next-btn'}
            appearance="primary"
            onClick={handleNext}
          >
            <FormattedMessage {...messages.nextButtonLabel} />
          </NextButton>
          <NoCreditCardRequiredText>
            <FormattedMessage {...messages.noCreditCardLabel} />
          </NoCreditCardRequiredText>
        </NextButtonContainer>
      );
    }
  };

  if (
    enableBundlesPostSignupConfluence &&
    setIsModalOpen &&
    setBundleProvisioningStatus &&
    isModalOpen !== undefined
  ) {
    return (
      <FreeBundlesDataProvider productData={productData}>
        <WacFreeBundlesModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          primaryProductKey={primaryProductKey}
          secondaryProductKeys={secondaryProductKeysOverride}
          bundleProvisioningStatus={bundleProvisioningStatus}
          setBundleProvisioningStatus={setBundleProvisioningStatus}
          selectedSecondaryProductKey={selectedSecondaryProductKey}
          setSelectedSecondaryProductKey={setSelectedSecondaryProductKey}
          isSecondaryProductActivating={isSecondaryProductActivating}
          handleAgreeAndSignup={handleAgreeAndSignup}
        ></WacFreeBundlesModal>
      </FreeBundlesDataProvider>
    );
  }

  return (
    <FreeBundlesDataProvider productData={productData}>
      <Wrapper>
        <AtlassianLogo
          size="large"
          iconColor={B200}
          textColor={B400}
          iconGradientStart={B400}
          iconGradientStop={B200}
        />
        <Root>
          <LeftColumn>
            <MainHeading>
              <FormattedMessage {...messages.freeBundlesMainHeader} />
            </MainHeading>
            <SubHeading>
              <FormattedMessage {...messages.freeBundlesSubHeader} />
            </SubHeading>
            <FeatureListContainer>
              <HeaderLevel4>
                <FormattedMessage {...messages.featureListHeader} />
              </HeaderLevel4>
              <ul>
                <li>
                  <FormattedMessage {...messages.supportDescription} />
                </li>
                <li>
                  <FormattedMessage {...messages.storageDescription} />
                </li>
                <li>
                  <FormattedMessage {...messages.communitySupportDescription} />
                </li>
                <li>
                  <FormattedMessage {...messages.noCreditCardDescription} />
                </li>
              </ul>
            </FeatureListContainer>
            {!onExperimentPage && (
              <LargerTeamCTAContainer className="desktop">
                <LargerTeamCTACopyContainer>
                  <Tooltip
                    component={HoverDialog}
                    content={
                      <FormattedMessage {...messages.largerTeamHoverText} />
                    }
                    position={'right'}
                  >
                    <h5>
                      <FormattedMessage {...messages.largerTeamLabel} />
                    </h5>
                  </Tooltip>
                  <a href="#" onClick={handleComparePlans}>
                    <FormattedMessage {...messages.comparePlansLabel} />
                  </a>
                </LargerTeamCTACopyContainer>
              </LargerTeamCTAContainer>
            )}
          </LeftColumn>
          <RightColumn>
            <HeaderLevel3>
              <FormattedMessage {...messages.primaryProductSelectedHeader} />
            </HeaderLevel3>
            <PrimaryProductDisplayView productKey={primaryProductKey} />
            <HeaderLevel3>
              <FormattedMessage
                {...messages.secondaryProductSelectionListHeader}
              />
            </HeaderLevel3>
            <SecondaryProductSelectList
              secondaryProductKeys={secondaryProductKeysOverride}
              selectedSecondaryProductKey={selectedSecondaryProductKey}
              setSelectedSecondaryProductKey={setSelectedSecondaryProductKey}
            />
            <ButtonGroup />
            {!onExperimentPage && (
              <LargerTeamCTAContainer className="mobile">
                <LargerTeamCTACopyContainer>
                  <Tooltip
                    component={HoverDialog}
                    content={
                      <FormattedMessage {...messages.largerTeamHoverText} />
                    }
                    position="right"
                  >
                    <h5>
                      <FormattedMessage {...messages.largerTeamLabel} />
                    </h5>
                  </Tooltip>
                  <a href="#" onClick={handleComparePlans}>
                    <FormattedMessage {...messages.comparePlansLabel} />
                  </a>
                </LargerTeamCTACopyContainer>
              </LargerTeamCTAContainer>
            )}
          </RightColumn>
        </Root>
      </Wrapper>
    </FreeBundlesDataProvider>
  );
}
